/**
 * FleetHub Login Screen - Web Version
 *
 * Email/password login for B2B fleet customers.
 * Features Google Maps background with animated hub locations and footer.
 *
 * @module screens/FleetHubLoginScreen
 */

import { FC, useState, useEffect } from "react"
import {
  View,
  ViewStyle,
  TextStyle,
  // eslint-disable-next-line no-restricted-imports
  TextInput,
  ActivityIndicator,
  useWindowDimensions,
  TouchableOpacity,
} from "react-native"
import { useNavigation, useRoute, type RouteProp } from "@react-navigation/native"
import type { NativeStackNavigationProp } from "@react-navigation/native-stack"
import {
  signInWithEmailAndPassword,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signOut,
} from "firebase/auth"
import { observer } from "mobx-react-lite"

import { Button } from "@/components/Button"
import { FleetHubLogo } from "@/components/FleetHubLogo"
import { Footer } from "@/components/Footer"
import { Icon } from "@/components/Icon"
import { Text } from "@/components/Text"
import { TrackingMapView } from "@/components/TrackingMapView.web"
import { useStores } from "@/models"
import type { AppStackParamList } from "@/navigators/AppNavigator"
import { resolveFleetHubReturnRoute } from "@/navigators/fleetHubReturnTo"
import { auth } from "@/services/firebase/auth"
import {
  FLEETHUB_ACCESS_REVOKED_MESSAGE,
  FLEETHUB_WRONG_ACCOUNT_MESSAGE,
  isBlockedFromFleetHub,
} from "@/services/firebase/b2bAccess"
import { colors, spacing, typography, breakpoints } from "@/theme"

export interface FleetHubLoginScreenProps {
  /**
   * Callback after successful login
   */
  onLoginSuccess?: () => void
}

/**
 * FleetHub Login Screen - Web Version
 *
 * Features:
 * - Google Maps background with animated hub locations
 * - White login card overlay
 * - Footer with Impressum/Datenschutz links
 * - Firebase Authentication
 */
export const FleetHubLoginScreen: FC<FleetHubLoginScreenProps> = observer(({ onLoginSuccess }) => {
  const navigation = useNavigation<NativeStackNavigationProp<AppStackParamList>>()
  const { width } = useWindowDimensions()
  const isMobile = width < breakpoints.mobile

  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [isResetMode, setIsResetMode] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [resetLoading, setResetLoading] = useState(false)

  // Explains an involuntary return from the dashboard (PLA-534 recovery path).
  const route = useRoute<RouteProp<AppStackParamList, "FleetHubLogin">>()
  const [error, setError] = useState(
    route.params?.reason === "b2b-access-revoked" ? FLEETHUB_ACCESS_REVOKED_MESSAGE : "",
  )
  // PLA-861: deep links to a protected FleetHub sub-route come back here after login.
  const afterLoginRoute = resolveFleetHubReturnRoute(route.params?.returnTo)

  // Access order tracking store for map locations
  const { orderTrackingStore } = useStores()

  /**
   * Load all hub locations on mount for map background
   */
  useEffect(() => {
    orderTrackingStore.loadAllLocations()
  }, [])

  /**
   * Check if user is already logged in on mount
   * Uses onAuthStateChanged to wait for Firebase to restore auth from storage (async)
   */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) return
      // PLA-534: a *restored* session bypasses handleLogin entirely, so the claim
      // check has to run here too — otherwise a poisoned session (an internal ops
      // account signed in earlier) is auto-forwarded to a dashboard that only 403s.
      void (async () => {
        if (await isBlockedFromFleetHub(user)) {
          await signOut(auth)
          setError(FLEETHUB_WRONG_ACCOUNT_MESSAGE)
          return
        }
        console.log("FleetHub Login: User already authenticated, redirecting to dashboard")
        navigation.replace(afterLoginRoute)
      })()
    })

    return () => unsubscribe()
  }, [navigation, afterLoginRoute])

  /**
   * Handle login submission using Firebase Authentication
   */
  const handleLogin = async () => {
    setIsLoading(true)
    setError("")

    try {
      // Validate inputs
      if (!email || !email.includes("@")) {
        setError("Bitte gib eine gültige E-Mail-Adresse ein")
        setIsLoading(false)
        return
      }

      if (!password || password.length < 6) {
        setError("Passwort muss mindestens 6 Zeichen lang sein")
        setIsLoading(false)
        return
      }

      // Authenticate with Firebase (retry on cold-start timeout)
      const MAX_RETRIES = 2
      let lastError: any = null

      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
          const userCredential = await signInWithEmailAndPassword(auth, email, password)

          // PLA-534: one Firebase project backs internal/ops, B2B and admin users,
          // so a valid credential proves nothing about *which* kind just signed in.
          // Advisory only — the server stays the authority; this keeps the wrong
          // kind of user off a dashboard where every callable would 403.
          if (await isBlockedFromFleetHub(userCredential.user)) {
            await signOut(auth)
            setError(FLEETHUB_WRONG_ACCOUNT_MESSAGE)
            return
          }

          console.log("FleetHub login successful:", userCredential.user.uid)
          navigation.replace(afterLoginRoute)
          onLoginSuccess?.()
          return
        } catch (err: any) {
          lastError = err
          const isColdStart =
            err.code === "auth/internal-error" && err.message?.toLowerCase().includes("deadline")
          if (isColdStart && attempt < MAX_RETRIES) {
            await new Promise((r) => setTimeout(r, 1500))
            continue
          }
          break
        }
      }

      // Handle Firebase Auth error codes
      const err = lastError
      let errorMessage = "Anmeldung fehlgeschlagen. Bitte überprüf deine Zugangsdaten."

      switch (err.code) {
        case "auth/invalid-credential":
          errorMessage = "Ungültige Anmeldedaten. Bitte überprüf E-Mail und Passwort."
          break
        case "auth/user-not-found":
          errorMessage = "Kein Benutzerkonto mit dieser E-Mail-Adresse gefunden."
          break
        case "auth/wrong-password":
          errorMessage = "Falsches Passwort. Bitte versuchen Sie es erneut."
          break
        case "auth/invalid-email":
          errorMessage = "Ungültige E-Mail-Adresse."
          break
        case "auth/user-disabled":
          errorMessage = "Dieses Konto wurde deaktiviert. Bitte kontaktier deinen Administrator."
          break
        case "auth/too-many-requests":
          errorMessage =
            "Zu viele Anmeldeversuche. Bitte warten Sie einen Moment und versuchen Sie es erneut."
          break
        case "auth/network-request-failed":
          errorMessage = "Netzwerkfehler. Bitte überprüf deine Internetverbindung."
          break
        default:
          errorMessage = err.message || errorMessage
      }

      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Handle password reset submission
   */
  const handlePasswordReset = async () => {
    if (!email || !email.includes("@")) {
      setError("Bitte gib eine gültige E-Mail-Adresse ein")
      return
    }

    setResetLoading(true)
    setError("")

    try {
      await sendPasswordResetEmail(auth, email.toLowerCase().trim())
      setError("")
      alert(`Passwort-Reset-Link wurde an ${email} gesendet. Bitte überprüfe dein Postfach.`)
      setIsResetMode(false)
    } catch (err: any) {
      let errorMessage = "Fehler beim Senden des Reset-Links."

      if (err.code === "auth/user-not-found") {
        errorMessage = "Kein Konto mit dieser E-Mail gefunden."
      } else if (err.code === "auth/invalid-email") {
        errorMessage = "Ungültige E-Mail-Adresse."
      } else if (err.code === "auth/too-many-requests") {
        errorMessage = "Zu viele Anfragen. Bitte warte einen Moment."
      }

      setError(errorMessage)
    } finally {
      setResetLoading(false)
    }
  }

  /**
   * Handle Enter key press for form submission
   */
  const handleKeyPress = (e: any) => {
    if (e.key === "Enter" && !isLoading && email && password) {
      handleLogin()
    }
  }

  return (
    <View style={$container}>
      {/* Google Maps Background - Layer 1 */}
      <View style={$mapContainer}>
        <TrackingMapView
          showAllLocations={true}
          allLocations={orderTrackingStore.allLocations.slice()}
          driverLocation={null}
          customerLocation={null}
          pickupLocation={null}
          showRoute={false}
        />
      </View>

      {/* Login Card Content - Layer 2 */}
      <View style={[$contentContainer, isMobile && $contentContainerMobile]}>
        {/* Private Customer Banner */}
        <View style={$privateBanner}>
          <Text style={$privateBannerText}>
            Kein Geschäftskunde?{" "}
            <Text
              style={$privateBannerLink}
              onPress={() => window.open("https://www.holdeineplakette.de", "_blank")}
            >
              Zur Privatkunden Terminbuchung
            </Text>
          </Text>
        </View>

        {/* Login Form with Header Inside */}
        <View style={$form}>
          {/* Header inside white card */}
          <View style={$header}>
            <FleetHubLogo width={isMobile ? 180 : 240} />
            <Text style={$subtitle}>
              {isResetMode ? "Passwort zurücksetzen" : "Anmeldung für Geschäftskunden"}
            </Text>
          </View>

          {isResetMode ? (
            // PASSWORD RESET VIEW
            <>
              {/* Email Input Only */}
              <View style={$inputGroup}>
                <Text style={$label}>E-Mail</Text>
                <TextInput
                  style={$input}
                  placeholder="deine.email@firma.de"
                  value={email}
                  onChangeText={setEmail}
                  autoCapitalize="none"
                  keyboardType="email-address"
                  autoComplete="email"
                  editable={!resetLoading}
                />
              </View>

              {/* Error Message */}
              {error && (
                <View style={$errorContainer}>
                  <Text style={$errorText}>{error}</Text>
                </View>
              )}

              {/* Reset Button */}
              <Button
                text={resetLoading ? "" : "Passwort zurücksetzen"}
                onPress={handlePasswordReset}
                disabled={resetLoading || !email}
                preset="filled"
                style={$loginButton}
              >
                {resetLoading && (
                  <ActivityIndicator size="small" color={colors.palette.neutral100} />
                )}
              </Button>

              {/* Back to Login Button */}
              <Button
                text="Zurück zum Login"
                onPress={() => {
                  setIsResetMode(false)
                  setError("")
                }}
                preset="default"
                style={$backButton}
              />
            </>
          ) : (
            // LOGIN VIEW
            <>
              {/* Email Input */}
              <View style={$inputGroup}>
                <Text style={$label}>E-Mail</Text>
                <TextInput
                  style={$input}
                  placeholder="deine.email@firma.de"
                  value={email}
                  onChangeText={setEmail}
                  autoCapitalize="none"
                  keyboardType="email-address"
                  autoComplete="email"
                  editable={!isLoading}
                  onKeyPress={handleKeyPress}
                />
              </View>

              {/* Password Input */}
              <View style={$inputGroup}>
                <Text style={$label}>Passwort</Text>
                <View style={$passwordInputContainer}>
                  <TextInput
                    style={$passwordInput}
                    placeholder="••••••••"
                    value={password}
                    onChangeText={setPassword}
                    secureTextEntry={!showPassword}
                    autoComplete="password"
                    editable={!isLoading}
                    onKeyPress={handleKeyPress}
                  />
                  <TouchableOpacity
                    style={$eyeIconContainer}
                    onPress={() => setShowPassword(!showPassword)}
                  >
                    <Icon icon={showPassword ? "view" : "hidden"} size={20} color={colors.text} />
                  </TouchableOpacity>
                </View>
              </View>

              {/* Error Message */}
              {error && (
                <View style={$errorContainer}>
                  <Text style={$errorText}>{error}</Text>
                </View>
              )}

              {/* Login Button */}
              <Button
                text={isLoading ? "" : "Anmelden"}
                onPress={handleLogin}
                disabled={isLoading || !email || !password}
                preset="filled"
                style={$loginButton}
              >
                {isLoading && <ActivityIndicator size="small" color={colors.palette.neutral100} />}
              </Button>

              {/* Help Text with Reset Link */}
              <Text style={$helpText}>
                <Text
                  style={$linkText}
                  onPress={() =>
                    window.open(
                      "https://marimba-gadget-jester.heyflow.site/holdeineplakette---fleethub-registrierung",
                      "_blank",
                    )
                  }
                >
                  Registriere dich
                </Text>
                ,{" "}
                <Text style={$linkText} onPress={() => setIsResetMode(true)}>
                  setze dein Passwort zurück
                </Text>{" "}
                oder ruf uns gerne an und wir helfen dir: 0911 880 8306
              </Text>
            </>
          )}
        </View>
      </View>

      {/* Footer - Layer 3 */}
      <View style={$footer}>
        <Footer />
      </View>
    </View>
  )
})

// ============================================================================
// Styles
// ============================================================================

const $container: ViewStyle = {
  flex: 1,
  position: "relative",
  backgroundColor: colors.background,
  height: "100vh" as any, // Web-specific viewport height
  width: "100%",
}

const $mapContainer: ViewStyle = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 0, // Layer 1: Map background
}

const $contentContainer: ViewStyle = {
  flex: 1,
  justifyContent: "center",
  alignItems: "center",
  paddingHorizontal: spacing.xl,
  maxWidth: 480,
  width: "100%",
  alignSelf: "center",
  zIndex: 10, // Layer 2: Login card above map
}

const $contentContainerMobile: ViewStyle = {
  paddingHorizontal: spacing.md,
}

const $header: ViewStyle = {
  alignItems: "center",
  marginBottom: spacing.xl,
}

const $subtitle: TextStyle = {
  fontSize: 16,
  color: colors.textDim,
  marginTop: spacing.md,
  fontWeight: "600",
}

const $form: ViewStyle = {
  width: "100%",
  backgroundColor: colors.surface, // White card
  borderRadius: 16,
  padding: spacing.xl,
  shadowColor: colors.palette.neutral900,
  shadowOffset: { width: 0, height: 4 },
  shadowOpacity: 0.15, // Slightly stronger shadow for visibility on map
  shadowRadius: 16,
  elevation: 6, // Higher elevation for layered design
}

const $inputGroup: ViewStyle = {
  marginBottom: spacing.lg,
}

const $label: TextStyle = {
  fontSize: 14,
  fontWeight: "500",
  color: colors.text,
  marginBottom: spacing.xs,
}

const $input: ViewStyle & TextStyle = {
  height: 56,
  borderWidth: 1,
  borderColor: colors.border,
  borderRadius: 8,
  paddingHorizontal: spacing.md,
  fontSize: 16,
  color: colors.text,
  backgroundColor: colors.surface,
  fontFamily: typography.primary.normal,
}

const $passwordInputContainer: ViewStyle = {
  position: "relative",
}

const $passwordInput: ViewStyle & TextStyle = {
  height: 56,
  borderWidth: 1,
  borderColor: colors.border,
  borderRadius: 8,
  paddingHorizontal: spacing.md,
  paddingRight: 50, // Make room for eye icon
  fontSize: 16,
  color: colors.text,
  backgroundColor: colors.surface,
  fontFamily: typography.primary.normal,
}

const $eyeIconContainer: ViewStyle = {
  position: "absolute",
  right: 15,
  top: 18, // Vertically center in 56px height input
  width: 24,
  height: 24,
  justifyContent: "center",
  alignItems: "center",
}

const $errorContainer: ViewStyle = {
  backgroundColor: colors.errorBackground,
  borderRadius: 8,
  padding: spacing.md,
  marginBottom: spacing.md,
}

const $errorText: TextStyle = {
  fontSize: 14,
  color: colors.palette.neutral100,
  textAlign: "center",
}

const $loginButton: ViewStyle = {
  marginTop: spacing.md,
  backgroundColor: colors.primary,
  borderWidth: 0,
}

const $helpText: TextStyle = {
  fontSize: 13,
  color: colors.textDim,
  textAlign: "center",
  marginTop: spacing.lg,
}

const $linkText: TextStyle = {
  fontSize: 13, // Same as $helpText
  color: colors.primary, // #FF382F red
  fontWeight: "600",
  textDecorationLine: "underline",
}

const $backButton: ViewStyle = {
  marginTop: spacing.md,
  backgroundColor: colors.palette.neutral800, // Dark grey/black background
  borderWidth: 1,
  borderColor: colors.palette.neutral800,
}

const $footer: ViewStyle = {
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 2000, // Layer 3: Footer on top
}

const $privateBanner: ViewStyle = {
  width: "100%",
  backgroundColor: colors.surface,
  borderRadius: 16,
  paddingVertical: spacing.md,
  paddingHorizontal: spacing.lg,
  marginBottom: spacing.md,
  shadowColor: colors.palette.neutral900,
  shadowOffset: { width: 0, height: 2 },
  shadowOpacity: 0.1,
  shadowRadius: 8,
  elevation: 3,
}

const $privateBannerText: TextStyle = {
  fontSize: 14,
  color: colors.text,
  textAlign: "center",
}

const $privateBannerLink: TextStyle = {
  fontSize: 14,
  color: colors.primary,
  fontWeight: "600",
  textDecorationLine: "underline",
}
